import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AnimationItem } from 'lottie-web';
import { AnimationOptions } from 'ngx-lottie';
import { DynamicLottie } from 'lottie-json-helper/lib/dynamic-lottie';
import { IDynamicIdentifier, IDynamicLottieChange } from 'lottie-json-helper/lib/types';
import {
    DynamicLottieEditorComponent,
    IDynamicLottieEditorData,
} from '../../../components/dialogs/dynamic-lottie-editor/dynamic-lottie-editor.component';

/**
 * Generic lottie-json-helper testbed for the live Angular environment.
 *
 *   1) Paste a Lottie JSON (e.g. the production DynamicBanner-10.json).
 *   2) Load — the tester runs DynamicLottie.findFullDynamicNodes() to extract
 *      every dynamic identifier (text, fill, slider, etc.) and renders the
 *      animation through ngx-lottie + our sfl-lottie-web fork.
 *   3) Edit Dynamics opens the production DynamicLottieEditorComponent dialog
 *      against those identifiers. On save we feed the updated changes to
 *      DynamicLottie.updateLottieJson() and re-load the resulting JSON.
 *
 * The whole point is to reproduce production-runtime behavior without needing
 * a project/scene/take — same lib, same fork, same renderer.
 */
@Component({
    selector: 'app-lottie-tester',
    templateUrl: './lottie-tester.component.html',
    styleUrls: ['./lottie-tester.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LottieTesterComponent {
    /** Pasted JSON (raw string, before parse). */
    pastedJson = '';
    /** Validation/status banner. */
    status = '' as string;
    statusKind: 'idle' | 'ok' | 'err' = 'idle';

    /** Live ng-lottie options. Bound to the most recently loaded JSON. */
    options: AnimationOptions | null = null;
    /** Quick render stats once an animation is loaded. */
    info: { w: number; h: number; frames: number; fps: number; layers: number; identifiers: number } | null = null;
    /** Identifiers extracted via lottie-json-helper; passed to the editor as IDynamicLottieChange[]. */
    private identifiers: IDynamicIdentifier[] = [];
    /** User-edited changes (start as a clone of identifiers; mutated by the editor). */
    private changes: IDynamicLottieChange[] = [];
    /** Live anim instance, only used to safely destroy() before re-loading. */
    private anim: AnimationItem | null = null;

    constructor(
        private readonly dialog: MatDialog,
        private readonly cdr: ChangeDetectorRef
    ) {}

    // ── lifecycle from ng-lottie ──
    onAnimationCreated(anim: AnimationItem): void {
        this.anim = anim;
    }

    get hasIdentifiers(): boolean {
        return this.changes.length > 0;
    }

    // ── user actions ──
    loadFromTextarea(): void {
        const raw = (this.pastedJson || '').trim();
        if (!raw) {
            this.fail('Paste a Lottie JSON above first.');
            return;
        }
        let parsed: any;
        try {
            parsed = JSON.parse(raw);
        } catch (e: any) {
            this.fail('Not valid JSON: ' + (e?.message ?? e));
            return;
        }
        if (!Array.isArray(parsed.layers) || !parsed.w || !parsed.h) {
            this.fail('JSON parsed but does not look like a Lottie animation (missing layers / w / h).');
            return;
        }

        // Extract identifiers via the same path production uses.
        let identifiers: IDynamicIdentifier[] = [];
        try {
            const items = DynamicLottie.findFullDynamicNodes(parsed, false);
            identifiers = items.flatMap((it: any) => it.identifiers || []);
            // De-duplicate by id so a measure-twin doesn't surface duplicate rows.
            const seen = new Set<string>();
            identifiers = identifiers.filter((i) => {
                if (!i?.id || seen.has(i.id)) return false;
                seen.add(i.id);
                return true;
            });
        } catch (e: any) {
            // Non-fatal — render still works without identifiers.
            this.flash('Loaded, but identifier extraction failed: ' + (e?.message ?? e), 'err');
        }

        this.identifiers = identifiers;
        // Editor mutates change objects in place; clone so re-opening the editor
        // doesn't carry stale state between sessions.
        this.changes = identifiers.map((i) => ({ ...i }));

        this.applyJson(parsed);
        this.info = {
            w: parsed.w,
            h: parsed.h,
            frames: parsed.op ?? 0,
            fps: parsed.fr ?? 0,
            layers: parsed.layers.length,
            identifiers: identifiers.length,
        };
        this.flash(
            `Loaded ${parsed.w}×${parsed.h}, ${parsed.layers.length} layers, ${identifiers.length} dynamic identifier(s).`,
            'ok'
        );
    }

    openEditor(): void {
        if (!this.changes.length) {
            this.fail('Load a JSON with dynamic identifiers first.');
            return;
        }
        const data: IDynamicLottieEditorData = { changes: this.changes };
        const ref = this.dialog.open(DynamicLottieEditorComponent, {
            width: '750px',
            maxHeight: '90vh',
            panelClass: 'dynamic-lottie-editor-dialog',
            data,
        });
        ref.afterClosed().subscribe((updated: IDynamicLottieChange[] | null) => {
            if (!updated) return;
            this.changes = updated;
            // Re-apply against the pasted base each time (idempotent), so iterative
            // edits don't compound mutations from previous applies.
            let baseStr: string;
            try {
                // re-stringify a parsed clone — guarantees the base is the user's paste
                JSON.parse(this.pastedJson);
                baseStr = this.pastedJson;
            } catch {
                this.fail('Could not re-parse pasted JSON for re-apply.');
                return;
            }
            let updatedStr: string;
            try {
                updatedStr = DynamicLottie.updateLottieJson(baseStr, updated);
            } catch (e: any) {
                this.fail('updateLottieJson failed: ' + (e?.message ?? e));
                return;
            }
            this.applyJson(JSON.parse(updatedStr));
            this.flash(`Re-rendered with ${updated.length} change(s) applied.`, 'ok');
        });
    }

    clearAll(): void {
        this.pastedJson = '';
        this.options = null;
        this.info = null;
        this.identifiers = [];
        this.changes = [];
        this.status = '';
        this.statusKind = 'idle';
        if (this.anim) {
            try {
                this.anim.destroy();
            } catch {}
            this.anim = null;
        }
        this.cdr.markForCheck();
    }

    // ── internal ──
    private applyJson(animationData: any): void {
        // Destroy the previous animation before replacing the options reference so
        // lottie-web doesn't keep two instances in memory.
        if (this.anim) {
            try {
                this.anim.destroy();
            } catch {}
            this.anim = null;
        }
        // New object identity => ngx-lottie sees a change and reloads.
        this.options = {
            animationData,
            autoplay: true,
            loop: true,
            renderer: 'svg',
        };
        this.cdr.markForCheck();
    }

    private flash(msg: string, kind: 'ok' | 'err'): void {
        this.status = msg;
        this.statusKind = kind;
        this.cdr.markForCheck();
    }

    private fail(msg: string): void {
        this.flash(msg, 'err');
    }
}
